// The roster and everything about lineups: browsing fighters, building lineups, and picking one to fight with.
import type { LineupSaved, Ok, Player } from '../src/api.js';
import type { FighterClass, Fighter, Skill } from '../src/content/fighters.js';
import { General, type GeneralSkill } from '../src/content/general.js';
import type { Line, LineupSlot, SavedLineup } from '../src/game/lineup.js';
import { Drag } from './drag.js';
import { Page } from './page.js';
import { Session } from './session.js';
import { type Html, Ui } from './ui.js';

const { api, h, setHtml, el, elOf, toast, attempt } = Ui;
const { catalog } = Session;

const LINES: Line[] = ['front', 'back'];
const LINE_NAMES: Record<Line, string> = { front: 'Frontline', back: 'Backline' };
const CLASSES = [...new Set(catalog.fighters.map(w => w.class))];
const byClassAndName = catalog.fighters.toSorted(
  (a, b) => a.class.localeCompare(b.class) || a.name.localeCompare(b.name),
);

let classFilter: FighterClass | null = null;

const filteredFighters = (): Fighter[] => byClassAndName.filter(w => !classFilter || w.class === classFilter);

const classFilterHtml = (): Html => {
  const chip = (c: FighterClass | null, label: string) =>
    h`<button class="chip" data-class="${c ?? ''}" data-filter="${c ?? ''}" aria-pressed="${String(classFilter === c)}">${label}</button>`;
  return h`<div class="filters" role="group" aria-label="Filter by class">
    ${chip(null, 'All')}${CLASSES.map(c => chip(c, c))}</div>`;
};

/** `render` redraws the fighter grid after the filter changes. */
const bindClassFilter = (root: HTMLElement, render: () => void): void => {
  root.querySelectorAll<HTMLButtonElement>('[data-filter]').forEach(b => {
    b.onclick = () => {
      classFilter = CLASSES.find(c => c === b.dataset.filter) ?? null;
      root.querySelectorAll('[data-filter]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
      render();
    };
  });
};

/** Buttons to switch between the player's saved lineups. Bind them with `bindChips`. */
const chipsHtml = (lineups: SavedLineup[], activeId: number | null): Html =>
  h`<div class="filters lineup-chips" role="group" aria-label="Saved lineups">${lineups.map(
    l => h`<button class="chip" data-lineup="${l.id}" aria-pressed="${String(l.id === activeId)}">${l.name}</button>`,
  )}</div>`;

const bindChips = (root: HTMLElement, onPick: (id: number) => void): void => {
  root.querySelectorAll<HTMLButtonElement>('[data-lineup]').forEach(b => {
    b.onclick = () => onPick(Number(b.dataset.lineup));
  });
};

const previewHtml = (slots: LineupSlot[] | null): Html => {
  const cards = (slots ?? []).flatMap(s => {
    const w = Session.fighter(s.fighterId);
    return w
      ? [
          h`<div class="preview-card" data-class="${w.class}"><img src="/art/${w.id}/idle" alt="">
            <div class="name">${w.name}</div><div class="muted sub">${LINE_NAMES[s.line]}</div></div>`,
        ]
      : [];
  });
  return cards.length
    ? h`<div class="lineup-preview">${cards}</div>`
    : h`<p class="muted">You have no lineup saved yet.</p>`;
};

/** The player's lineup chips, when there is a choice, over a preview of the active lineup. */
const pickerHtml = (player: Player): Html => {
  const picked = Session.activeLineup(player);
  return h`${player.lineups.length > 1 ? chipsHtml(player.lineups, picked?.id ?? null) : ''}
    ${previewHtml(picked ? Session.playable(picked) : null)}`;
};

const cardHtml = (w: Fighter): Html =>
  h`<article class="card" data-class="${w.class}">
    <img class="art" src="/art/${w.id}/splash" alt="${w.name}" loading="lazy">
    <span class="class-badge">${w.class}</span>
    <div class="info">
      <div class="name">${w.name}</div>
      <div class="title">${w.title}</div>
      ${Ui.fighterDetails(w)}
    </div>
  </article>`;

const roster = (): void => {
  setHtml(
    Page.view,
    h`<h2>Roster</h2>
    <p class="muted">All ${catalog.fighters.length} fighters are yours to pick from.</p>
    ${classFilterHtml()}
    <div class="grid" id="rosterGrid"></div>`,
  );
  const render = () => setHtml(el('#rosterGrid'), h`${filteredFighters().map(cardHtml)}`);
  bindClassFilter(Page.view, render);
  render();
};

// ---------- Lineup editor ----------

interface EditorState {
  player: Player;
  /** The lineup being edited. A new one has no id until it is saved. */
  id: number | null;
  name: string;
  picks: LineupSlot[];
  /** The lineup as last opened or saved, to tell when there are unsaved changes. */
  saved: string;
  /** The fighter shown in the info panel. */
  selected: string;
  /** A skill the player just tried to turn on while all slots were taken. */
  refused: string | null;
}

const snapshot = (state: EditorState): string => JSON.stringify({ name: state.name.trim(), picks: state.picks });
const isDirty = (state: EditorState): boolean => snapshot(state) !== state.saved;
const lineOf = (state: EditorState, id: string): Line | undefined => state.picks.find(s => s.fighterId === id)?.line;

const slotOf = (state: EditorState, id: string): LineupSlot | undefined => state.picks.find(s => s.fighterId === id);

const defaultSkills = (w: Fighter): string[] => w.skills.slice(0, catalog.skillsPerFighter).map(s => s.id);
const skillsInUse = (w: Fighter, s: LineupSlot): string[] => s.skills ?? defaultSkills(w);
const hasOwnSkills = (w: Fighter, s: LineupSlot): boolean => skillsInUse(w, s).join() !== defaultSkills(w).join();

/** Keeps picks in order, her own first, and leaves them out when they are the default, as the server saves them. */
const setSkills = (w: Fighter, s: LineupSlot, ids: string[]): void => {
  const ordered = [...w.skills, ...General.all].map(skill => skill.id).filter(id => ids.includes(id));
  if (ordered.join() === defaultSkills(w).join()) delete s.skills;
  else s.skills = ordered;
};

/** The first picked fighter still short of skills. */
const shortOfSkills = (state: EditorState): Fighter | undefined =>
  state.picks.flatMap(s => {
    const w = Session.fighter(s.fighterId);
    return w && skillsInUse(w, s).length < catalog.skillsPerFighter ? [w] : [];
  })[0];

const inTagHtml = (state: EditorState, id: string): Html | null => {
  const line = lineOf(state, id);
  return line ? h`<span class="in-tag">${LINE_NAMES[line]}</span>` : null;
};

const lineCardHtml = (state: EditorState, s: LineupSlot): Html | null => {
  const w = Session.fighter(s.fighterId);
  if (!w) return null;
  return h`<div class="line-card ${w.id === state.selected ? 'selected' : ''}" data-class="${w.class}" data-drag="${w.id}" data-slot="${w.id}" data-select="${w.id}">
      <img src="/art/${w.id}/idle" alt="" draggable="false">
      <div class="name">${w.name}</div>
      <button class="bench" data-bench="${w.id}" aria-label="Bench ${w.name}">×</button>
    </div>`;
};

const columnHtml = (state: EditorState, line: Line, hint: string): Html => {
  const cards = state.picks.filter(s => s.line === line).map(s => lineCardHtml(state, s));
  return h`<section class="line ${line}" data-zone="${line}">
      <h3>${LINE_NAMES[line]}</h3>
      <p class="muted sub">${hint}</p>
      <div class="line-cards">${cards.length ? cards : h`<div class="line-empty muted">Drop fighters here</div>`}</div>
    </section>`;
};

const linesHtml = (state: EditorState): Html =>
  h`${columnHtml(state, 'back', 'Safe until the frontline falls')}${columnHtml(state, 'front', 'Takes the hits')}`;

const fighterRowHtml = (state: EditorState, w: Fighter): Html =>
  h`<div class="fighter-row ${lineOf(state, w.id) ? 'picked' : ''} ${w.id === state.selected ? 'selected' : ''}" data-class="${w.class}" data-drag="${w.id}" data-select="${w.id}" tabindex="0">
      <img class="slice" src="/art/${w.id}/splash" alt="" loading="lazy" draggable="false">
      <div class="meta">
        <div class="name">${w.name} ${inTagHtml(state, w.id)}</div>
        <div class="class-tag">${w.class}</div>
        <div class="mini-stats">${Ui.STAT_KEYS.map(
          key =>
            h`<span class="${Ui.standing(w, catalog.fighters, key).trend}">${key.toUpperCase()} <b>${w.stats[key]}</b></span>`,
        )}</div>
      </div>
      <div class="add-line" role="group" aria-label="Put ${w.name} in a line">${LINES.map(line => {
        const here = lineOf(state, w.id) === line;
        return h`<button class="add-btn" data-add="${w.id}" data-line="${line}" aria-pressed="${String(here)}" aria-label="${here ? `${w.name} is in the ${LINE_NAMES[line].toLowerCase()}` : `Put ${w.name} in the ${LINE_NAMES[line].toLowerCase()}`}">${line === 'front' ? 'Front' : 'Back'}</button>`;
      })}</div>
    </div>`;

const loadoutHintHtml = (state: EditorState, inUse: string[]): Html => {
  const max = catalog.skillsPerFighter;
  if (state.refused)
    return h`<p class="loadout-hint refused" role="alert">Only ${max} skills can be equipped at a time. Unequip one first.</p>`;
  if (inUse.length < max) return h`<p class="loadout-hint short">Equip ${max - inUse.length} more.</p>`;
  return h`<p class="loadout-hint">Click a skill to equip or unequip it.</p>`;
};

const skillOf = (w: Fighter, id: string): Skill | GeneralSkill | undefined =>
  w.skills.find(s => s.id === id) ?? General.find(id);

const slotHtml = (skill: Skill | GeneralSkill | undefined): Html =>
  skill
    ? h`<div class="skill-slot ${skill.on === 'general' ? 'general' : 'signature'}">
        <span class="slot-kind">${skill.on === 'general' ? 'General' : 'Signature'}</span>
        <b class="slot-name">${skill.name}</b>
        <span class="slot-text">${skill.on === 'general' ? skill.effect : Ui.triggerLabel[skill.on]}</span>
        <button class="slot-drop" data-skill-toggle="${skill.id}" aria-label="Unequip ${skill.name}">Unequip</button>
      </div>`
    : h`<div class="skill-slot empty">Empty slot<span>Equip a skill below</span></div>`;

const skillRowHtml = (state: EditorState, skill: Skill | GeneralSkill, inUse: string[]): Html => {
  const used = inUse.includes(skill.id);
  const full = inUse.length >= catalog.skillsPerFighter;
  return h`<div class="skill-row ${used ? 'in-use' : 'spare'} ${state.refused === skill.id ? 'refused' : ''}">
      <div class="skill-row-text">${Ui.skillsHtml([{ skill }])}</div>
      <button class="loadout-state" data-skill-toggle="${skill.id}" aria-pressed="${String(used)}" aria-label="${used ? 'Unequip' : 'Equip'} ${skill.name}">
        <span class="now">${used ? 'Equipped' : full ? 'Slots full' : 'Equip'}</span><span class="next">${used ? 'Unequip' : full ? 'Slots full' : 'Equip'}</span>
      </button>
    </div>`;
};

/** A general skill is just a name and a boost, so it gets a compact tile; the details are on hover. */
const generalTileHtml = (state: EditorState, skill: GeneralSkill, inUse: string[]): Html => {
  const used = inUse.includes(skill.id);
  const full = !used && inUse.length >= catalog.skillsPerFighter;
  return h`<button class="general-tile ${used ? 'in-use' : ''} ${full ? 'full' : ''} ${state.refused === skill.id ? 'refused' : ''}" data-skill-toggle="${skill.id}" aria-pressed="${String(used)}" data-tip="${skill.text}">
      <b>${skill.name}</b><span class="boost">${skill.effect}</span>
    </button>`;
};

/** The two equipped skills, then everything she could equip instead: her own skills and the general ones. */
const loadoutHtml = (state: EditorState, w: Fighter, s: LineupSlot): Html => {
  const inUse = skillsInUse(w, s);
  const slots = Array.from({ length: catalog.skillsPerFighter }, (_, i) => slotHtml(skillOf(w, inUse[i] ?? '')));
  return h`<div class="loadout-head">
        <h3>${w.name}’s skills <span class="${inUse.length < catalog.skillsPerFighter ? 'short' : ''}">${inUse.length} of ${catalog.skillsPerFighter} equipped</span></h3>
        <button class="link-btn ${hasOwnSkills(w, s) ? '' : 'invisible'}" data-skill-reset>Reset to default</button>
      </div>
      <div class="skill-slots">${slots}</div>
      ${loadoutHintHtml(state, inUse)}
      <div class="skill-group signature">
        <div class="group-head"><span>Signature</span><span>Hers only</span></div>
        ${w.skills.map(skill => skillRowHtml(state, skill, inUse))}
      </div>
      <div class="skill-group general">
        <div class="group-head"><span>General</span><span>Any fighter · raises her stats</span></div>
        <div class="general-grid">${General.all.map(skill => generalTileHtml(state, skill, inUse))}</div>
      </div>`;
};

const skillsPanelHtml = (state: EditorState, w: Fighter): Html => {
  const slot = slotOf(state, w.id);
  if (slot) return loadoutHtml(state, w, slot);
  return h`<div class="loadout-head"><h3>${w.name}’s skills</h3></div>
      <p class="loadout-hint">Put her in a line to pick which ${catalog.skillsPerFighter} she brings. Her first ${catalog.skillsPerFighter} are the default.</p>
      ${Ui.skillsHtml(w.skills.map(skill => ({ skill })))}`;
};

const fighterInfoHtml = (state: EditorState, w: Fighter): Html => {
  const slot = slotOf(state, w.id);
  const boosted = slot
    ? General.apply(
        w.stats,
        General.all.filter(g => skillsInUse(w, slot).includes(g.id)),
      )
    : w.stats;
  return h`<img class="art" src="/art/${w.id}/splash" alt="${w.name}">
      <div class="body">
        <span class="class-badge">${w.class}</span> ${inTagHtml(state, w.id)}
        <h3 class="name">${w.name}</h3>
        <div class="title">${w.title}</div>
        ${Ui.statBars(w, catalog.fighters, boosted)}
        ${Ui.critHtml(w, boosted)}
      </div>`;
};

const facesHtml = (slots: LineupSlot[]): Html[] =>
  Array.from({ length: catalog.teamSize }, (_, i) => {
    const w = Session.fighter(slots[i]?.fighterId ?? '');
    return w
      ? h`<span class="face" data-class="${w.class}"><img src="/art/${w.id}/idle" alt="" draggable="false"></span>`
      : h`<span class="face empty"></span>`;
  });

const tileHtml = (state: EditorState, id: number | null, name: string, slots: LineupSlot[]): Html => {
  const current = id === state.id;
  const dirty = current && isDirty(state);
  return h`<button class="lineup-tile ${dirty ? 'dirty' : ''}" ${id === null ? '' : h`data-lineup="${id}"`} aria-pressed="${String(current)}">
      <span class="faces">${facesHtml(slots)}</span>
      <span class="tile-name">${name}</span>
      <span class="tile-meta">${id === null ? h`<span class="new-tag">New</span> ` : ''}${dirty ? 'Unsaved changes' : `${slots.length} of ${catalog.teamSize} fighters`}</span>
    </button>`;
};

/** The unsaved new lineup while one is being made, otherwise a button to start one. */
const newTileHtml = (state: EditorState): Html => {
  if (state.id === null) return tileHtml(state, null, state.name.trim() || 'New lineup', state.picks);
  const full = state.player.lineups.length >= catalog.maxLineups;
  return h`<button class="lineup-tile add" id="newLineup" ${full ? 'disabled' : ''}>
      <span class="plus" aria-hidden="true">+</span>
      <span class="tile-name">New lineup</span>
      <span class="tile-meta">${full ? 'Delete one to make room' : 'Start from scratch'}</span>
    </button>`;
};

const shelfHtml = (state: EditorState): Html => {
  const { lineups } = state.player;
  return h`<div class="shelf-head"><h3>Your lineups</h3><span class="muted">${lineups.length} of ${catalog.maxLineups} saved</span></div>
    <div class="shelf" role="group" aria-label="Your lineups">${lineups.map(l =>
      l.id === state.id
        ? tileHtml(state, l.id, state.name.trim() || l.name, state.picks)
        : tileHtml(state, l.id, l.name, Session.playable(l)),
    )}${newTileHtml(state)}</div>`;
};

const editorHtml = (): Html =>
  h`<div id="builder"><h2>Lineups</h2>
    <p class="muted">Pick one when you enter a tournament. Drag up to ${catalog.teamSize} fighters into your lines. Drop onto a fighter to swap, or drag one out to bench her.</p>
    <section class="lineup-shelf" id="lineupTabs"></section>
    <div class="lineup-layout">
      <aside class="fighter-picker">
        ${classFilterHtml()}
        <div class="fighter-list" id="fighterList" data-zone="bench"></div>
      </aside>
      <section class="lineup-lines">
        <div class="lineup-bar">
          <input class="field" id="lineupName" maxlength="24" aria-label="Lineup name" placeholder="Lineup name">
          <span class="muted" id="lineupStatus"></span>
          <button class="btn ghost small" id="deleteLineup">Delete</button>
          <button class="btn small" id="save">Save lineup</button>
        </div>
        <div class="lines" id="lines"></div>
      </section>
      <div class="fighter-detail">
        <section class="fighter-info" id="fighterInfo" aria-label="Fighter" aria-live="polite"></section>
        <section class="skills-panel" id="loadout" aria-label="Skills"></section>
      </div>
    </div>
    <p class="muted">Every round each fighter attacks once,
      fastest first. It finishes off anyone it surely kills, otherwise it picks a random target it can reach, leaning towards the injured. After ${catalog.turnLimit} rounds the team with more of its health left wins. If both teams fall at once, the last of each to fall gets back up on 1 HP for a sudden death.</p></div>`;

/**
 * Moves fighter `id` into `line`, swapping places with `onto` when she is dropped on a picked fighter.
 * Without a line she goes back to the bench. Returns false when the lineup is already full.
 */
const placeFighter = (picks: LineupSlot[], id: string, line: Line | undefined, onto: string | undefined): boolean => {
  const from = picks.findIndex(s => s.fighterId === id);
  const to = picks.findIndex(s => s.fighterId === onto);
  if (!line) {
    if (from >= 0) picks.splice(from, 1);
  } else if (to >= 0 && to !== from) {
    const onTo = picks[to];
    const moving = from >= 0 ? picks[from] : { fighterId: id, line: onTo.line };
    if (from >= 0) picks[from] = { ...onTo, line: moving.line };
    picks[to] = { ...moving, line: onTo.line };
  } else if (from < 0 && picks.length >= catalog.teamSize) {
    return false;
  } else {
    if (from >= 0) picks.splice(from, 1);
    picks.push({ fighterId: id, line });
  }
  return true;
};

const editor = (player: Player): void => {
  const state: EditorState = {
    player,
    id: null,
    name: '',
    picks: [],
    saved: '',
    selected: byClassAndName[0].id,
    refused: null,
  };

  setHtml(Page.view, editorHtml());
  const builder = el('#builder');
  const nameInput = elOf(HTMLInputElement, '#lineupName');

  const open = (l: SavedLineup | undefined) => {
    state.id = l?.id ?? null;
    state.name = l?.name ?? `Lineup ${player.lineups.length + 1}`;
    if (l) Session.setActiveLineup(l.id);
    state.picks = l ? Session.playable(l).map(slot => ({ ...slot })) : [];
    state.saved = snapshot(state);
    state.selected = state.picks[0]?.fighterId ?? state.selected;
    nameInput.value = state.name;
  };
  const mayLeave = () => !isDirty(state) || confirm('Discard your unsaved changes?');

  const renderInfo = () => {
    const w = Session.fighter(state.selected);
    if (!w) return;
    const info = el('#fighterInfo');
    info.dataset.class = w.class;
    setHtml(info, fighterInfoHtml(state, w));
    const skills = el('#loadout');
    skills.dataset.class = w.class;
    setHtml(skills, skillsPanelHtml(state, w));
  };
  const renderList = () => setHtml(el('#fighterList'), h`${filteredFighters().map(w => fighterRowHtml(state, w))}`);
  const renderBar = () => {
    const isNew = state.id === null;
    const shelf = el('#lineupTabs');
    setHtml(shelf, shelfHtml(state));
    bindChips(shelf, id => {
      if (id === state.id || !mayLeave()) return;
      open(player.lineups.find(l => l.id === id));
      render();
    });
    const startNew = document.querySelector<HTMLButtonElement>('#newLineup');
    if (startNew)
      startNew.onclick = () => {
        if (!mayLeave()) return;
        open(undefined);
        render();
        nameInput.select();
      };
    const short = shortOfSkills(state);
    el('#lineupStatus').textContent = short
      ? `${short.name} needs ${catalog.skillsPerFighter} skills`
      : `${state.picks.length} of ${catalog.teamSize} picked${isDirty(state) ? ' · unsaved changes' : ''}`;
    elOf(HTMLButtonElement, '#save').disabled =
      !state.picks.length || !state.name.trim() || !!short || (!isNew && !isDirty(state));
    const del = elOf(HTMLButtonElement, '#deleteLineup');
    del.hidden = isNew && !player.lineups.length;
    del.textContent = isNew ? 'Cancel' : 'Delete';
  };
  const render = () => {
    state.refused = null;
    setHtml(el('#lines'), linesHtml(state));
    renderBar();
    renderList();
    renderInfo();
  };

  const select = (id: string) => {
    state.selected = id;
    state.refused = null;
    builder
      .querySelectorAll<HTMLElement>('[data-select]')
      .forEach(x => x.classList.toggle('selected', x.dataset.select === id));
    renderInfo();
  };
  const place = (id: string, line: Line | undefined, onto?: string) => {
    if (!placeFighter(state.picks, id, line, onto)) {
      toast(`Your lineup is full. Bench a fighter first, or drop onto one to swap her out.`, true);
      return;
    }
    if (line) state.selected = id;
    render();
  };

  const changeSkills = (change: (inUse: string[], w: Fighter) => string[]) => {
    const w = Session.fighter(state.selected);
    const slot = slotOf(state, state.selected);
    if (!w || !slot) return;
    const next = change(skillsInUse(w, slot), w);
    state.refused = null;
    if (next.length > catalog.skillsPerFighter)
      state.refused = next.find(id => !skillsInUse(w, slot).includes(id)) ?? null;
    else setSkills(w, slot, next);
    renderBar();
    renderInfo();
  };

  const save = async () => {
    const body = { name: state.name, lineup: state.picks };
    const r = await (state.id === null
      ? api<LineupSaved>('/api/lineups', { method: 'POST', body })
      : api<LineupSaved>(`/api/lineups/${state.id}`, { method: 'PUT', body }));
    const at = player.lineups.findIndex(l => l.id === r.saved.id);
    if (at >= 0) player.lineups[at] = r.saved;
    else player.lineups.push(r.saved);
    open(r.saved);
    render();
    toast(`${r.saved.name} saved`);
  };
  /** A new lineup is only cancelled; a saved one is deleted for good. */
  const remove = async () => {
    const id = state.id;
    if (id === null) {
      if (mayLeave()) open(Session.activeLineup(player));
    } else {
      if (!confirm(`Delete ${state.name}?`)) return;
      await api<Ok>(`/api/lineups/${id}`, { method: 'DELETE' });
      player.lineups = player.lineups.filter(l => l.id !== id);
      open(player.lineups[0]);
      toast('Lineup deleted');
    }
    render();
  };

  Drag.attach({
    root: builder,
    source: '[data-drag]',
    zone: '[data-zone]',
    ghost: id => `/art/${id}/idle`,
    drop: (id, zone, hit) =>
      place(
        id,
        LINES.find(l => l === zone?.dataset.zone),
        hit?.closest<HTMLElement>('[data-slot]')?.dataset.slot,
      ),
  });
  builder.onclick = ev => {
    const target = ev.target instanceof Element ? ev.target : null;
    const bench = target?.closest<HTMLElement>('[data-bench]')?.dataset.bench;
    const pick = target?.closest<HTMLElement>('[data-select]')?.dataset.select;
    const skill = target?.closest<HTMLElement>('[data-skill-toggle], [data-skill-reset]')?.dataset;
    const add = target?.closest<HTMLElement>('[data-add]')?.dataset;
    const addLine = LINES.find(l => l === add?.line);
    if (add?.add && addLine) {
      if (lineOf(state, add.add) !== addLine) place(add.add, addLine);
    } else if (bench) place(bench, undefined);
    else if (pick) select(pick);
    else if (skill?.skillToggle) {
      const id = skill.skillToggle;
      changeSkills(inUse => (inUse.includes(id) ? inUse.filter(x => x !== id) : [...inUse, id]));
    } else if (skill && 'skillReset' in skill) changeSkills((_, w) => defaultSkills(w));
  };
  builder.onkeydown = ev => {
    const pick = ev.target instanceof HTMLElement ? ev.target.dataset.select : undefined;
    if (pick && (ev.key === 'Enter' || ev.key === ' ')) {
      ev.preventDefault();
      select(pick);
    }
  };
  bindClassFilter(builder, renderList);
  nameInput.oninput = () => {
    state.name = nameInput.value;
    renderBar();
  };
  el('#save').onclick = () => attempt(save);
  el('#deleteLineup').onclick = () => attempt(remove);

  open(Session.activeLineup(player));
  render();
};

export const Lineups = { roster, editor, chipsHtml, bindChips, previewHtml, pickerHtml };
